var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createStyles, makeStyles } from '@hawkins/styles';
var generateThemeVariables = function (theme) {
    var hawkins = theme.hawkins;
    if (hawkins.theme === 'light') {
        return {
            colorBackground: hawkins.ui.backgroundColor0,
            borderColor: 'transparent',
            color: hawkins.textColor.textColorRegular,
        };
    }
    return {
        colorBackground: hawkins.elevation.backgroundColor3,
        color: hawkins.textColor.textColorLight,
        borderColor: hawkins.elevation.borderColorElevated,
    };
};
export var dropdownStyles = function (theme) {
    var hawkins = theme.hawkins, hawkinsVariables = theme.hawkinsVariables;
    var themeVariables = generateThemeVariables(theme);
    return createStyles({
        /** Class name to apply to the root of the dropdown, it is the wrapper of the label + description + input + list combined */
        root: {
            display: 'inline-block',
            position: 'relative',
            width: '100%', // fill parent container by default
        },
        /** Class name to apply to the dropdown list */
        dropdown: {
            margin: 0,
            zIndex: 10,
            right: 0,
            width: '100%',
        },
        /**
         * Class name applied to the div rendered inside of
         * the FloatingUI context that houses the dropdown options
         */
        dropdownList: {
            overflowY: 'auto',
        },
        /**
         * Class name applied to the div rendered inside of
         * the FloatingUI context that houses the dropdown options
         * for the virtualized variant
         */
        dropdownListVirtualized: {
            flexGrow: 1,
            minHeight: 0,
        },
        /**
         * Class name applied to the inner div rendered inside of the
         * dropdownList div
         */
        dropdownListBody: {
            overflowY: 'auto',
        },
        /**
         * Class name applied to the inner div rendered inside of the
         * dropdownList div for the virtualized variant
         */
        dropdownListBodyVirtualized: {
            flexGrow: 1,
            minHeight: 0,
            overflowY: 'hidden',
        },
        /**
         * Class name applied to the div that houses the virtualized list.
         * This is exclusive to the virtualized variant.
         */
        dropdownVirtualizedListWrapper: {
            flexGrow: 1,
        },
        /** Class name to apply to the dropdown list container that has the options */
        dropdownContainer: {
            padding: 0,
            margin: 0,
            overflow: 'hidden',
            // overflow: 'auto',
            borderRadius: "calc(".concat(hawkinsVariables.borderRadius.borderRadius0, " - ").concat(hawkins.elevation.borderWidthElevated, ")"),
            '& .Hawkins-dropdown-group-label': {
                position: 'sticky',
                top: -2,
                width: '100%',
                zIndex: 2,
                backgroundColor: hawkins.elevation.backgroundColor2,
            },
        },
        /**
         * Class name applied to the grouping header when grouping the options list
         */
        groupLabel: {},
        /** class name to apply to the input trigger when multiline is true */
        inputTriggerMultiline: {},
        /** Class name to apply to the input trigger (not search input) */
        input: {
            '&&': {
                display: 'flex',
                justifyContent: 'space-between',
                height: 40,
                padding: 0,
                cursor: 'pointer',
                width: '100%',
            },
            '& > input[type="text"]': {
                zIndex: '-1',
                position: 'absolute',
                left: 0,
                right: 0,
                background: 'transparent',
                cursor: 'pointer',
            },
            '&.Hawkins-multi-with-value': {
                height: 'auto',
            },
            '&.Hawkins-Compact': {
                height: hawkinsVariables.sizings.size3,
            },
            '&.Hawkins-dropdown-borderless': {
                padding: "0 ".concat(hawkinsVariables.spacings.space1),
                '&:hover': {
                    backgroundColor: hawkins.state.backgroundColorHover,
                },
            },
            '&.multiSelected': {
                minWidth: 'min-content',
            },
            '&.disabled': {
                '&&': {
                    cursor: 'not-allowed',
                },
                '& > input[type="text"]': {
                    cursor: 'not-allowed',
                },
            },
        },
        /** Class name to apply to the form field container of the input trigger */
        formFieldContainer: {},
        /** Class name to apply to the clear button */
        clearButton: {
            padding: 0,
            marginLeft: hawkinsVariables.spacings.space0,
            marginRight: hawkinsVariables.spacings.space0,
        },
        /** Class name to apply to the clear button when dropdown is disabled */
        clearButtonDisabled: {
            padding: 0,
            marginLeft: hawkinsVariables.spacings.space0,
            marginRight: hawkinsVariables.spacings.space0,
            pointerEvents: 'none',
            cursor: 'not-allowed',
            opacity: 0.5,
        },
        /** Class name to apply the the divider between the clear button and caret */
        divider: {
            width: 1,
            background: function (_a) {
                var clearable = _a.clearable;
                return clearable ? hawkins.ui.borderColorXlight : 'transparent';
            },
            alignSelf: 'stretch',
            margin: "0 ".concat(hawkinsVariables.spacings.space1, " 0 ").concat(hawkinsVariables.spacings.space0),
            '&.Hawkins-dropdown-borderless': {
                background: 'transparent',
            },
        },
        /** Class name to apply to the floatingUI component (default dropdown list wrapper) */
        floatingUi: {
            padding: 0,
            backgroundColor: themeVariables.colorBackground,
            borderColor: themeVariables.borderColor,
            borderRadius: hawkinsVariables.borderRadius.borderRadius0,
            borderStyle: 'solid',
            borderWidth: 1,
            boxShadow: theme.hawkins.elevation.boxShadow2,
            color: theme.hawkins.textColor.textColorRegular,
            fontSize: hawkinsVariables.fontSizes.fontSize2,
        },
        /** Class name to apply to the list item container in the dropdown list */
        listItemContainer: {
            width: '100%',
            '&:hover': {
                backgroundColor: hawkins.state.backgroundColorHover,
            },
            '&.multi': {
                overflow: 'initial',
                '& > div': {
                    marginRight: 4,
                },
            },
            '&.selected': {
                cursor: 'pointer',
            },
            '&.disabled': {
                color: hawkins.textColor.textColorXlight,
                cursor: 'default',
            },
            '&.hovered': {
                backgroundColor: hawkins.state.backgroundColorHover,
            },
        },
        /** Class name to apply to the label in the list item */
        listItemLabelParagraph: {},
        /** Class name to apply to the label in the list item when it is disabled */
        listItemLabelParagraphDisabled: {
            color: hawkins.textColor.textColorXlight,
        },
        /** Class name to apply to the wrapper of the dropdown trigger label when dropdown is single-select */
        singleSelectLabelWrapper: {
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            gridGap: hawkinsVariables.spacings.space1,
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 'inherit',
        },
        /** Class name to apply to the wrapper of the dropdown trigger label when dropdown is multi-select */
        multiSelectLabelWrapper: {
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            gridGap: hawkinsVariables.spacings.space2,
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 'inherit',
        },
        /** Class name to apply to the label of the dropdown trigger */
        labelWrapper: {
            minWidth: '40px',
            paddingRight: hawkinsVariables.spacings.space1,
            display: 'grid',
            fontSize: 'inherit',
        },
        /** Class name to apply to the label text the dropdown trigger when not navigational */
        labelText: {
            fontSize: 'inherit',
        },
        /** Class name to apply to the create option */
        createOption: {
            alignItems: 'center',
            backgroundColor: hawkins.highlight.backgroundColorRegular,
            borderTopColor: hawkins.ui.borderColorXlight,
            borderTopStyle: 'solid',
            borderTopWidth: 1,
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'row',
            gridGap: hawkinsVariables.spacings.space1,
            paddingBottom: hawkinsVariables.spacings.space1,
            paddingTop: hawkinsVariables.spacings.space1,
            '&.Hawkins-MenuItem': {
                fontWeight: hawkins.body.fontWeightBold,
                '&, &[data-focus=true]': {
                    color: hawkins.textColor.textColorInteractiveRegular,
                },
            },
            '& .Hawkins-Icon': {
                position: 'relative',
                top: -1,
            },
        },
        /** Class name to apply to the button link in creatable button link */
        createOptionButtonLink: {
            fontWeight: 'bold',
            paddingLeft: hawkinsVariables.spacings.space2,
            paddingRight: hawkinsVariables.spacings.space2,
            '&:focus': {
                color: hawkins.textColor.textColorInteractiveRegular,
            },
        },
        /** Class name to apply to the container of the start adornment component */
        startAdornmentContainer: {},
        endAdornmentContainer: {
            alignSelf: 'stretch',
            paddingRight: hawkinsVariables.spacings.space1,
        },
        /** Class name to apply to the container of the selection chips */
        chipsContainer: {},
        // The styles below are Duplicated from label - Review and remove the ones that are not applied here
        /** Class name to apply to the label container for single*/
        labelContainer: {
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
            marginRight: hawkinsVariables.spacings.space1,
        },
        /** Class name to apply to the label container for multi */
        labelContainerMulti: {
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
            // marginLeft: `-${hawkinsVariables.spacings.space2}`,
            '&.hasValue': {
                display: 'flex',
                marginLeft: "-".concat(hawkinsVariables.spacings.space1),
            },
            '&.multiline': {
                marginLeft: "-".concat(hawkinsVariables.spacings.space2),
                paddingTop: hawkinsVariables.spacings.space0,
            },
            '& > $chipsContainer': {
                display: 'inline-grid',
            },
        },
        /** Class name to apply to the delete icon in the default chip for multi-select */
        multiLabelChipClearIcon: {},
        /** Class name to apply to the delete icon in the default chip for multi-select when dropdown is disabled */
        multiLabelChipClearIconDisabled: {
            pointerEvents: 'none',
            cursor: 'not-allowed',
        },
        /** Class name to apply to default excess count chip component when dropdown is multi-select and not multiline  */
        chipsLabelWithoutTruncation: {
            overflow: 'visible',
        },
    });
};
export var dropdownLabelStyles = function (theme) {
    var hawkins = theme.hawkins, hawkinsVariables = theme.hawkinsVariables;
    return createStyles({
        /** Class name to apply to the container of the selection chips */
        chipsContainer: {},
        /** Class name to apply to the root of the label */
        root: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            '&.multi': {
                overflow: 'initial',
                display: 'flex',
                flexWrap: 'wrap',
                whiteSpace: 'unset',
                marginTop: 0,
                marginRight: 0,
                '& > *': {
                    marginBottom: hawkinsVariables.spacings.space0,
                    marginLeft: hawkinsVariables.spacings.space0,
                },
            },
            '&.empty': {
                color: hawkins.textColor.textColorLight,
                opacity: 1,
                // marginTop: hawkinsVariables.spacings.space0,
            },
            '&.disabled': {
                color: hawkins.textColor.textColorXlight,
            },
        },
        /** Class name to apply to the label container for single*/
        labelContainer: {
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
            marginRight: hawkinsVariables.spacings.space1,
        },
        /** Class name to apply to the label container for multi */
        labelContainerMulti: {
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
            // marginLeft: `-${hawkinsVariables.spacings.space2}`,
            '&.hasValue': {
                display: 'flex',
                marginLeft: "-".concat(hawkinsVariables.spacings.space1),
            },
            '&.multiline': {
                marginLeft: "-".concat(hawkinsVariables.spacings.space2),
                padding: "3px 0",
                marginBottom: "-".concat(hawkinsVariables.spacings.space0), // we need this "hack" to balance the margin bottom that comes from the general multi container.
            },
            '& > $chipsContainer': {
                display: 'inline-grid',
            },
        },
        /** Class name to apply to the delete icon in the default chip for multi-select */
        multiLabelChipClearIcon: {},
        /** Class name to apply to the delete icon in the default chip for multi-select when dropdown is disabled */
        multiLabelChipClearIconDisabled: {
            pointerEvents: 'none',
            cursor: 'not-allowed',
        },
        /** Class name to apply to the container of the start adornment component */
        startAdornmentContainer: {},
        chipsLabelWithoutTruncation: {
            overflow: 'visible',
        },
    });
};
export var useDropdownStyles = makeStyles(dropdownStyles, { name: 'Hawkins-Dropdown' });
export var useDropdownLabelStyles = makeStyles(dropdownLabelStyles, {
    name: 'Hawkins-DropdownLabel',
});
/**
 * Styles for search input in the dropdown list
 * @param theme
 * @returns
 */
export var searchInputStyles = function (theme) {
    var hawkins = theme.hawkins, hawkinsVariables = theme.hawkinsVariables;
    var psuedoFocusBorder = {
        '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            borderWidth: hawkinsVariables.borderWidthValues.borderWidth0,
            bottom: "calc(".concat(hawkins.focus.borderWidthRegular, " * -1)"),
            borderBottomWidth: hawkins.focus.borderWidthRegular,
            borderBottomColor: hawkins.focus.borderColorRegular,
            borderBottomStyle: 'solid',
            pointerEvents: 'none',
            zIndex: hawkinsVariables.zIndexes.modal,
        },
    };
    return {
        root: {
            marginBottom: 1,
            '&&': {
                backgroundColor: 'transparent',
                borderBottomColor: hawkins.ui.borderColorXlight,
                borderBottomStyle: 'solid',
                borderBottomWidth: '1px',
                borderColor: 'transparent',
                borderLeftWidth: 0,
                borderRadius: 0,
                borderRightWidth: 0,
                borderStyle: 'solid',
                borderWidth: hawkinsVariables.borderWidthValues.borderWidth0,
                bottom: "calc(".concat(hawkins.focus.borderWidthRegular, " * -1)"),
                marginBottom: '2px',
            },
            '&:focus-within': __assign({ position: 'relative', borderLeftWidth: 0, borderRightWidth: 0 }, psuedoFocusBorder),
        },
        rootActive: __assign({}, psuedoFocusBorder),
        focusRingAnchor: {
            display: 'none',
        },
    };
};
export var useSearchInputStyles = makeStyles(searchInputStyles, {
    name: 'Hawkins-DropdownSearchInput',
});
